import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAnalysisStore } from '@/store/analysisStore'
import { useAuthStore } from '@/store/authStore'
import { useSurveyStore } from '@/store/surveyStore'
import { analyzeSkinPhoto, scoresToConcerns } from '@/services/perfectcorp'
import { photoToFile } from '@/services/photoStorage'
import { trackFunnelEvent, trackError } from '@/services/analytics'
import { TEST_PHOTO_ID, TEST_FULL_RESULTS } from '@/lib/testFixtures'
import { EducationalLoader } from '@/components/analysis/EducationalLoader'
import { haptic } from '@/lib/haptics'
import type { SkinType } from '@/types/analysis'

// Decorative sparkle with animation
const Sparkle = ({
  className,
  size = 24,
  color = '#FF8C33',
  delay = 0
}: {
  className?: string
  size?: number
  color?: string
  delay?: number
}) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    initial={{ scale: 0, opacity: 0, rotate: -30 }}
    animate={{
      scale: [0, 1.3, 1],
      opacity: [0, 1, 0.8],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 0.6,
      delay: delay,
      ease: "easeOut"
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Confetti piece component
const ConfettiPiece = ({
  delay,
  x,
  color
}: {
  delay: number
  x: number
  color: string
}) => (
  <motion.div
    className="absolute top-0 w-3 h-3 rounded-sm"
    style={{
      left: `${x}%`,
      backgroundColor: color,
    }}
    initial={{ y: -20, opacity: 0, rotate: 0 }}
    animate={{
      y: ['0vh', '100vh'],
      opacity: [0, 1, 1, 0],
      rotate: [0, 360, 720],
      x: [0, Math.random() * 40 - 20, Math.random() * 60 - 30]
    }}
    transition={{
      duration: 3 + Math.random() * 2,
      delay: delay,
      ease: "easeIn"
    }}
  />
)

// Animated checkmark in circle with sparkle burst
const AnimatedCheckmark = () => (
  <div className="relative w-[120px] h-[120px] flex items-center justify-center">
    {/* Glow effect */}
    <motion.div
      className="absolute inset-0 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(76, 217, 100, 0.3) 0%, rgba(76, 217, 100, 0) 70%)',
      }}
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: [1, 1.3, 1.1], opacity: [0, 0.8, 0.5] }}
      transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
    />

    {/* Sparkle rays bursting out */}
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 rounded-full bg-[#4CD964]"
        style={{
          top: '50%',
          left: '50%',
        }}
        initial={{ x: '-50%', y: '-50%', scale: 0, opacity: 0 }}
        animate={{
          x: `calc(-50% + ${Math.cos((i * Math.PI * 2) / 8) * 70}px)`,
          y: `calc(-50% + ${Math.sin((i * Math.PI * 2) / 8) * 70}px)`,
          scale: [0, 1.2, 0],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: 0.8,
          delay: 0.5 + i * 0.05,
          ease: "easeOut",
        }}
      />
    ))}

    {/* Small sparkles */}
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={`small-${i}`}
        className="absolute w-1.5 h-1.5 rounded-full"
        style={{
          top: '50%',
          left: '50%',
          backgroundColor: ['#FF8C33', '#46C0C6', '#FFB8B8', '#7EC8E3'][i % 4],
        }}
        initial={{ x: '-50%', y: '-50%', scale: 0, opacity: 0 }}
        animate={{
          x: `calc(-50% + ${Math.cos((i * Math.PI * 2) / 8 + Math.PI / 8) * 85}px)`,
          y: `calc(-50% + ${Math.sin((i * Math.PI * 2) / 8 + Math.PI / 8) * 85}px)`,
          scale: [0, 1, 0],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: 0.7,
          delay: 0.6 + i * 0.04,
          ease: "easeOut",
        }}
      />
    ))}

    {/* Circle */}
    <motion.svg
      width="90"
      height="90"
      viewBox="0 0 90 90"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.4, delay: 0.1, type: "spring", stiffness: 200 }}
    >
      {/* Background circle */}
      <motion.circle
        cx="45"
        cy="45"
        r="42"
        fill="#4CD964"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      />

      {/* Circle border drawing animation */}
      <motion.circle
        cx="45"
        cy="45"
        r="42"
        stroke="white"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
      />

      {/* Checkmark */}
      <motion.path
        d="M25 46 L38 59 L65 32"
        stroke="white"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.4, delay: 0.5, ease: "easeOut" }}
      />
    </motion.svg>
  </div>
)

// Confetti colors matching brand
const CONFETTI_COLORS = ['#FF8C33', '#46C0C6', '#E8A87C', '#7EC8E3', '#4CD964', '#FFB8B8']

export function CelebrationPage() {
  const navigate = useNavigate()
  const { analysisResults, setAnalysisResults, photoId, hasHDResults } = useAnalysisStore()
  const { shortSurvey } = useSurveyStore()
  const { isAuthenticated, hasPaid, demoMode } = useAuthStore()
  const [countdown, setCountdown] = useState(5)
  const hasRedirected = useRef(false)

  // Analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(true) // Start with loading
  const [analysisProgress, setAnalysisProgress] = useState(0)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [analysisComplete, setAnalysisComplete] = useState(false)

  // Route guard
  useEffect(() => {
    if (hasRedirected.current) return

    if (!analysisResults) {
      hasRedirected.current = true
      navigate('/survey', { replace: true })
      return
    }

    if (!demoMode && (!isAuthenticated || !hasPaid)) {
      hasRedirected.current = true
      navigate('/payment', { replace: true })
    }
  }, [analysisResults, isAuthenticated, hasPaid, demoMode, navigate])

  // Run HD analysis on mount
  useEffect(() => {
    const runHDAnalysis = async () => {
      // If already have HD results, skip analysis and show celebration
      if (hasHDResults()) {
        console.log('[CelebrationPage] Already have HD results, showing celebration')
        setIsAnalyzing(false)
        setAnalysisComplete(true)
        return
      }

      // If no photo, can't run analysis
      if (!photoId) {
        console.log('[CelebrationPage] No photoId, skipping analysis')
        setIsAnalyzing(false)
        setAnalysisComplete(true)
        return
      }

      console.log('[CelebrationPage] Starting HD analysis for stored photo:', photoId)
      setAnalysisProgress(5)
      const analysisStartTime = Date.now()
      trackFunnelEvent.hdAnalysisStarted()

      try {
        // TEST MODE: Skip API call and use pre-saved test data
        if (photoId === TEST_PHOTO_ID) {
          console.log('[CelebrationPage] Test mode detected - using pre-saved HD results')
          setAnalysisProgress(50)

          // Simulate a short delay for UX
          await new Promise(resolve => setTimeout(resolve, 1500))

          setAnalysisProgress(90)

          // Use test results with current photo URL
          setAnalysisResults({
            ...TEST_FULL_RESULTS,
            photoUrl: analysisResults?.photoUrl || '',
            analyzedAt: new Date().toISOString(),
          })

          console.log('[CelebrationPage] Test HD results stored successfully')

          // Save full analysis to backend (non-blocking)
          useAnalysisStore.getState().saveToBackend('full')

          setAnalysisProgress(100)

          // Short delay before showing celebration
          await new Promise(resolve => setTimeout(resolve, 500))
          setIsAnalyzing(false)
          setAnalysisComplete(true)
          return
        }

        // NORMAL MODE: Call YCE API
        // Retrieve photo from S3
        const file = await photoToFile(photoId, 'photo.jpg')
        console.log('[CelebrationPage] Photo retrieved, size:', Math.round(file.size / 1024), 'KB')
        setAnalysisProgress(15)

        // Run HD analysis
        setAnalysisProgress(25)
        const apiResult = await analyzeSkinPhoto(file, (stage) => {
          // Update progress based on stage
          if (stage.includes('Uploading')) setAnalysisProgress(30)
          else if (stage.includes('Processing')) setAnalysisProgress(50)
          else if (stage.includes('Extracting')) setAnalysisProgress(70)
          else if (stage.includes('Complete')) setAnalysisProgress(90)
        }, 'full')

        console.log('[CelebrationPage] HD analysis complete:', apiResult.skinAge, apiResult.skinScore)

        // Convert to concern scores
        const concerns = scoresToConcerns(apiResult.scores)

        // Derive skin type
        const deriveSkinType = (): SkinType => {
          const oiliness = concerns.oiliness
          if (oiliness >= 70) return 'oily'
          if (oiliness >= 40) return 'combination'
          if (oiliness <= 20) return 'dry'
          return 'normal'
        }

        const skinType: SkinType =
          shortSurvey.skinType && shortSurvey.skinType !== 'not-sure'
            ? shortSurvey.skinType as SkinType
            : deriveSkinType()

        // Helper to find heatmaps
        const allHeatmapKeys = Object.keys(apiResult.heatmaps)
        const findHeatmap = (baseName: string): string => {
          const variations = [
            baseName, `${baseName}_whole`, `${baseName}_all`,
            `${baseName}_output`, `${baseName}_output_whole`, `${baseName}_output_all`,
            `hd_${baseName}`, `hd_${baseName}_whole`, `hd_${baseName}_all`,
          ]
          for (const key of variations) {
            if (apiResult.heatmaps[key]) return apiResult.heatmaps[key]
          }
          const partial = allHeatmapKeys.find(k => k.toLowerCase().includes(baseName.toLowerCase()))
          return partial ? apiResult.heatmaps[partial] : ''
        }

        // Convert zone scores to concern format (100 - ui_score)
        const wrinkleZoneScores = apiResult.scores.wrinkleZones ? {
          forehead: 100 - apiResult.scores.wrinkleZones.forehead,
          glabellar: 100 - apiResult.scores.wrinkleZones.glabellar,
          crowfeet: 100 - apiResult.scores.wrinkleZones.crowfeet,
          periocular: 100 - apiResult.scores.wrinkleZones.periocular,
          nasolabial: 100 - apiResult.scores.wrinkleZones.nasolabial,
          marionette: 100 - apiResult.scores.wrinkleZones.marionette,
        } : undefined

        const poreZoneScores = apiResult.scores.poreZones ? {
          forehead: 100 - apiResult.scores.poreZones.forehead,
          nose: 100 - apiResult.scores.poreZones.nose,
          cheek: 100 - apiResult.scores.poreZones.cheek,
        } : undefined

        // Update analysis results with HD data (ALL 14 metrics)
        setAnalysisResults({
          skinAge: apiResult.skinAge,
          skinScore: apiResult.skinScore,
          skinType,
          metrics: {
            // Core metrics
            pores: concerns.pores,
            texture: concerns.texture,
            pigmentation: concerns.spots,
            redness: concerns.redness,
            wrinkles: concerns.wrinkles,
            brightness: apiResult.scores.radiance, // Legacy field
            oiliness: concerns.oiliness,
            acne: concerns.acne,
            // HD-only metrics
            moisture: concerns.moisture,
            firmness: concerns.firmness,
            radiance: concerns.radiance,
            spots: concerns.spots,
            darkCircles: concerns.darkCircles,
            eyeBags: concerns.eyeBags,
            droopyEyelidUpper: concerns.droopyEyelidUpper,
            droopyEyelidLower: concerns.droopyEyelidLower,
          },
          heatmaps: {
            pores: findHeatmap('pore'),
            texture: findHeatmap('texture'),
            pigmentation: findHeatmap('age_spot') || findHeatmap('spot'),
            wrinkles: findHeatmap('wrinkle'),
            redness: findHeatmap('redness'),
            acne: findHeatmap('acne'),
            oiliness: findHeatmap('oiliness'),
            moisture: findHeatmap('moisture'),
            firmness: findHeatmap('firmness'),
            radiance: findHeatmap('radiance'),
            darkCircles: findHeatmap('dark_circle'),
            eyeBags: findHeatmap('eye_bag'),
            droopyEyelidUpper: findHeatmap('droopy_upper_eyelid'),
            droopyEyelidLower: findHeatmap('droopy_lower_eyelid'),
            // Zone-specific heatmaps (HD only)
            wrinkleZones: apiResult.zoneHeatmaps?.wrinkleZones,
            poreZones: apiResult.zoneHeatmaps?.poreZones,
          },
          photoUrl: analysisResults?.photoUrl || '',
          analyzedAt: new Date().toISOString(),
          // Zone-specific scores (HD only)
          wrinkleZoneScores,
          poreZoneScores,
        })

        console.log('[CelebrationPage] HD results stored successfully')

        // Save full analysis to backend (non-blocking)
        useAnalysisStore.getState().saveToBackend('full')

        setAnalysisProgress(100)
        trackFunnelEvent.hdAnalysisCompleted(Date.now() - analysisStartTime)

        // Short delay before showing celebration
        await new Promise(resolve => setTimeout(resolve, 500))
        setIsAnalyzing(false)
        setAnalysisComplete(true)
      } catch (error) {
        console.error('[CelebrationPage] HD analysis failed:', error)
        const errorMessage = error instanceof Error ? error.message : 'Analysis failed'
        trackError.analysis('HD_ANALYSIS_FAILED', errorMessage)
        setAnalysisError(errorMessage)
        setIsAnalyzing(false)
      }
    }

    runHDAnalysis()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId])

  // Auto-redirect countdown (only after analysis is complete)
  useEffect(() => {
    if (!analysisComplete) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          navigate('/report')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [analysisComplete, navigate])

  const handleViewResults = () => {
    haptic('success')
    navigate('/report')
  }

  const handleRetry = () => {
    haptic('light')
    setAnalysisError(null)
    setIsAnalyzing(true)
    setAnalysisProgress(0)
    // Trigger re-analysis by changing a dependency or just navigate back
    window.location.reload()
  }

  // Generate confetti pieces
  const confettiPieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.5,
    x: Math.random() * 100,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
  }))

  // Loading state - show educational loader during analysis
  if (isAnalyzing) {
    return (
      <EducationalLoader
        progress={analysisProgress}
        isComplete={analysisProgress >= 100}
      />
    )
  }

  // Error state
  if (analysisError) {
    return (
      <div className="min-h-dvh bg-[#FFFEF1] flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="text-[64px] mb-4">😔</div>
          <h1 className="text-[24px] font-bold text-[#075D61] mb-2">
            Analysis Failed
          </h1>
          <p className="text-[#688192] mb-6">
            {analysisError}
          </p>
          <button
            onClick={handleRetry}
            className="bg-[#46C0C6] hover:bg-[#3AACB2] text-white px-8 py-3 rounded-full font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Celebration state
  return (
    <div className="min-h-dvh bg-[#FFFEF1] flex flex-col overflow-hidden relative">
      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {confettiPieces.map((piece) => (
          <ConfettiPiece
            key={piece.id}
            delay={piece.delay}
            x={piece.x}
            color={piece.color}
          />
        ))}
      </div>

      {/* Header */}
      <header className="py-5 px-4 relative z-10">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[18px] text-center"
          style={{ fontFamily: "'Oleo Script', cursive" }}
        >
          <span className="text-[#688192]">K-Beauty</span>
          <span className="text-[#46C0C6]"> AI</span>
        </motion.h1>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        {/* Decorative sparkles */}
        <Sparkle className="absolute top-12 left-6" size={28} color="#FF8C33" delay={0.3} />
        <Sparkle className="absolute top-20 right-8" size={22} color="#46C0C6" delay={0.5} />
        <Sparkle className="absolute top-32 left-16" size={18} color="#E8A87C" delay={0.7} />
        <Sparkle className="absolute bottom-40 right-12" size={24} color="#7EC8E3" delay={0.4} />
        <Sparkle className="absolute bottom-52 left-10" size={20} color="#4CD964" delay={0.6} />
        <Sparkle className="absolute top-40 right-20" size={16} color="#FFB8B8" delay={0.8} />

        <div className="max-w-sm w-full text-center">
          {/* Celebration Icon */}
          <motion.div
            className="mb-6 flex justify-center"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <AnimatedCheckmark />
          </motion.div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h1
              className="text-[28px] text-black mb-2"
              style={{ fontFamily: "'Anybody', sans-serif", fontWeight: 600 }}
            >
              <span className="text-[#4CD964]">Congratulations!</span>
            </h1>
            <h2
              className="text-[22px] text-black mb-4"
              style={{ fontFamily: "'Anybody', sans-serif", fontWeight: 600 }}
            >
              Your results are{' '}
              <span className="text-[#FF8C33]">ready</span>
            </h2>
          </motion.div>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-[#688192] mb-8 leading-relaxed text-[16px]"
          >
            Your personalized K-beauty routine
            <br />
            is ready just for you!
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="flex flex-col gap-3 max-w-[280px] mx-auto"
          >
            <button
              onClick={handleViewResults}
              className="w-full bg-[#4CD964] hover:bg-[#3DBB53] text-white px-6 py-3.5 rounded-full font-bold text-[18px] active:scale-[0.98] transition-all shadow-lg shadow-[#4CD964]/30"
            >
              View My Results
            </button>

            {/* Countdown text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-[#A0A0A0] text-sm"
            >
              Redirecting in {countdown} seconds...
            </motion.p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
